import { useContext, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { AuthContext } from "~/utils/useAuth";
import { styled, keyframes } from "~/ui/style/stitches.config";
import {
  getAllGuidebookMeanings,
  updateGuidebookMeaning,
  createGuidebookMeaning,
  deleteGuidebookMeaning,
  GuidebookMeaning,
  meaningToMarkdown,
  markdownToMeaning,
  imagesToMarkdown,
  markdownToImages,
} from "~/api/guidebookApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";

const fadeIn = keyframes({
  '0%': { opacity: 0, transform: 'translateY(10px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' }
});

const fadeOut = keyframes({
  '0%': { opacity: 1 },
  '100%': { opacity: 0 }
});

export const GuidebookList = () => {
  const { user } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [meanings, setMeanings] = useState<GuidebookMeaning[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editOrder, setEditOrder] = useState(0);
  const [editImagePath, setEditImagePath] = useState("");
  const [editUpright, setEditUpright] = useState("");
  const [editReversed, setEditReversed] = useState("");
  const [editUprightImages, setEditUprightImages] = useState("");
  const [editReversedImages, setEditReversedImages] = useState("");

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 2500);
  };

  // Check if user is authorized
  useEffect(() => {
    if (user && !user.isAdmin) {
      setLocation("/404");
    }
  }, [user, setLocation]);

  // Fetch meanings
  useEffect(() => {
    const fetchMeanings = async () => {
      if (!user || !user.isAdmin) return;
      try {
        const data = await getAllGuidebookMeanings();
        setMeanings(data);
      } catch (error) {
        console.error("Failed to fetch meanings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMeanings();
  }, [user]);

  const startEdit = (meaning: GuidebookMeaning) => {
    setEditingSlug(meaning.slug);
    setEditName(meaning.name);
    setEditSlug(meaning.slug);
    setEditCategory(meaning.category);
    setEditOrder(meaning.order);
    setEditImagePath(meaning.imagePath || "");
    setEditUpright(meaningToMarkdown(meaning.upright));
    setEditReversed(meaning.reversed ? meaningToMarkdown(meaning.reversed) : "");
    setEditUprightImages(imagesToMarkdown(meaning.upright.images));
    setEditReversedImages(meaning.reversed ? imagesToMarkdown(meaning.reversed.images) : "");
  };

  const cancelEdit = () => {
    setEditingSlug(null);
    setIsCreating(false);
  };

  const saveEdit = async () => {
    if (!editingSlug) return;
    try {
      const uprightContent = markdownToMeaning(editUpright);
      const uprightImages = markdownToImages(editUprightImages);
      if (uprightImages) uprightContent.images = uprightImages;

      let reversedContent = editReversed.trim() ? markdownToMeaning(editReversed) : null;
      if (reversedContent) {
        const reversedImages = markdownToImages(editReversedImages);
        if (reversedImages) reversedContent.images = reversedImages;
      }

      const updated = await updateGuidebookMeaning(editingSlug, {
        name: editName,
        slug: editSlug,
        category: editCategory,
        order: editOrder,
        imagePath: editImagePath || null,
        upright: uprightContent,
        reversed: reversedContent,
      });
      setMeanings(meanings.map(m => m.slug === editingSlug ? updated : m));
      setEditingSlug(null);
      showToast("Saved ✓");
    } catch (error) {
      console.error("Failed to save:", error);
      showToast("Failed to save");
    }
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingSlug(null);
    setEditName("");
    setEditSlug("");
    setEditCategory("major");
    setEditOrder(0);
    setEditImagePath("");
    setEditUpright("keywords: \n\noverview: \n\n(energy): \n\n(actions): \n\n(strengths & challenges): \n\n(relationships): \n\n(work): \n\n(creativity): \n\n(home): \n\n(symbolism): \n\n(journal questions):\n");
    setEditReversed("");
    setEditUprightImages("");
    setEditReversedImages("");
  };

  const saveCreate = async () => {
    if (!editSlug.trim() || !editName.trim()) {
      showToast("Name and slug are required");
      return;
    }
    try {
      const uprightContent = markdownToMeaning(editUpright);
      const uprightImages = markdownToImages(editUprightImages);
      if (uprightImages) uprightContent.images = uprightImages;

      let reversedContent = editReversed.trim() ? markdownToMeaning(editReversed) : null;
      if (reversedContent) {
        const reversedImages = markdownToImages(editReversedImages);
        if (reversedImages) reversedContent.images = reversedImages;
      }

      const created = await createGuidebookMeaning({
        slug: editSlug,
        name: editName,
        category: editCategory,
        order: editOrder,
        imagePath: editImagePath || null,
        upright: uprightContent,
        reversed: reversedContent,
      });
      setMeanings([...meanings, created]);
      setIsCreating(false);
      showToast("Created ✓");
    } catch (error: any) {
      console.error("Failed to create:", error);
      showToast(error.response?.data?.error || "Failed to create");
    }
  };

  const handleDelete = async (slug: string, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deleteGuidebookMeaning(slug);
      setMeanings(meanings.filter(m => m.slug !== slug));
      setEditingSlug(null);
      showToast("Deleted");
    } catch (error) {
      console.error("Failed to delete:", error);
      showToast("Failed to delete");
    }
  };

  // Group meanings by category
  const grouped = meanings.reduce((acc, m) => {
    if (!acc[m.category]) acc[m.category] = [];
    acc[m.category].push(m);
    return acc;
  }, {} as Record<string, GuidebookMeaning[]>);

  const categoryOrder = ["major", "wands", "cups", "swords", "pentacles"];
  // Include any extra categories not in the predefined order
  const extraCategories = Object.keys(grouped).filter(c => !categoryOrder.includes(c));
  const allCategories = [...categoryOrder, ...extraCategories];

  if (!user || !user.isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <StyledPage>
        <PageContent>
          <SimpleLoadingIndicator />
        </PageContent>
      </StyledPage>
    );
  }

  return (
    <StyledPage>
      {toastMessage && <Toast>{toastMessage}</Toast>}
      <PageContent>
        <div className="container">
          <h1>Guidebook Meanings</h1>

          {!isCreating ? (
            <AddButton onClick={startCreate}>+ Add New Meaning</AddButton>
          ) : (
            <EditCard style={{ marginBottom: "30px" }}>
              <MinimizeButton onClick={cancelEdit}>−</MinimizeButton>
              <h3 style={{ margin: "0 0 15px", fontSize: "16px" }}>New Meaning</h3>
              <div className="meta-row">
                <div className="field">
                  <label>Name</label>
                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)} />
                </div>
                <div className="field small">
                  <label>Category</label>
                  <input type="text" value={editCategory} onChange={e => setEditCategory(e.target.value)} />
                </div>
                <div className="field small">
                  <label>Order</label>
                  <input type="number" value={editOrder} onChange={e => setEditOrder(parseInt(e.target.value) || 0)} />
                </div>
              </div>
              <div className="field">
                <label>Slug</label>
                <input type="text" value={editSlug} onChange={e => setEditSlug(e.target.value)} />
              </div>
              <div className="field">
                <label>Image Path</label>
                <input type="text" value={editImagePath} onChange={e => setEditImagePath(e.target.value)} />
              </div>
              <div className="meaning-row">
                <div className="field">
                  <label>Upright</label>
                  <textarea
                    value={editUpright}
                    onChange={e => setEditUpright(e.target.value)}
                    rows={12}
                  />
                </div>
                <div className="field">
                  <label>Reversed</label>
                  <textarea
                    value={editReversed}
                    onChange={e => setEditReversed(e.target.value)}
                    rows={12}
                  />
                </div>
              </div>
              <div className="meaning-row">
                <div className="field">
                  <label>Upright Images</label>
                  <textarea
                    value={editUprightImages}
                    onChange={e => setEditUprightImages(e.target.value)}
                    rows={4}
                    placeholder="meme images:&#10;- /decks/internet-lore/00c.jpg"
                  />
                </div>
                <div className="field">
                  <label>Reversed Images</label>
                  <textarea
                    value={editReversedImages}
                    onChange={e => setEditReversedImages(e.target.value)}
                    rows={4}
                    placeholder="meme images:&#10;- /decks/internet-lore/00c.jpg"
                  />
                </div>
              </div>
              <div className="button-row">
                <SaveButton onClick={saveCreate}>Create</SaveButton>
                <CancelButton onClick={cancelEdit}>Cancel</CancelButton>
              </div>
            </EditCard>
          )}

          {allCategories.map(category => {
            const items = grouped[category] || [];
            if (items.length === 0) return null;
            return (
              <Section key={category}>
                <h2>{category.charAt(0).toUpperCase() + category.slice(1)}</h2>
                <MeaningList>
                  {items.map(meaning =>
                    editingSlug === meaning.slug ? (
                      <EditCard key={meaning.slug}>
                        <MinimizeButton onClick={cancelEdit}>−</MinimizeButton>
                        <div className="meta-row">
                          <div className="field">
                            <label>Name</label>
                            <input
                              type="text"
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                            />
                          </div>
                          <div className="field small">
                            <label>Category</label>
                            <input
                              type="text"
                              value={editCategory}
                              onChange={e => setEditCategory(e.target.value)}
                            />
                          </div>
                          <div className="field small">
                            <label>Order</label>
                            <input
                              type="number"
                              value={editOrder}
                              onChange={e => setEditOrder(parseInt(e.target.value) || 0)}
                            />
                          </div>
                        </div>
                        <div className="field">
                          <label>Slug</label>
                          <input
                            type="text"
                            value={editSlug}
                            onChange={e => setEditSlug(e.target.value)}
                          />
                        </div>
                        <div className="field">
                          <label>Image Path</label>
                          <input
                            type="text"
                            value={editImagePath}
                            onChange={e => setEditImagePath(e.target.value)}
                          />
                        </div>
                        <div className="meaning-row">
                          <div className="field">
                            <label>Upright</label>
                            <textarea
                              value={editUpright}
                              onChange={e => setEditUpright(e.target.value)}
                              rows={12}
                            />
                          </div>
                          <div className="field">
                            <label>Reversed</label>
                            <textarea
                              value={editReversed}
                              onChange={e => setEditReversed(e.target.value)}
                              rows={12}
                            />
                          </div>
                        </div>
                        <div className="meaning-row">
                          <div className="field">
                            <label>Upright Images</label>
                            <textarea
                              value={editUprightImages}
                              onChange={e => setEditUprightImages(e.target.value)}
                              rows={4}
                              placeholder="meme images:&#10;- /decks/internet-lore/00c.jpg"
                            />
                          </div>
                          <div className="field">
                            <label>Reversed Images</label>
                            <textarea
                              value={editReversedImages}
                              onChange={e => setEditReversedImages(e.target.value)}
                              rows={4}
                              placeholder="meme images:&#10;- /decks/internet-lore/00c.jpg"
                            />
                          </div>
                        </div>
                        <div className="button-row">
                          <SaveButton onClick={saveEdit}>Save</SaveButton>
                          <CancelButton onClick={cancelEdit}>Cancel</CancelButton>
                          <DeleteButton onClick={() => handleDelete(editingSlug!, editName)}>Delete</DeleteButton>
                        </div>
                      </EditCard>
                    ) : (
                      <MeaningItem key={meaning.slug} onClick={() => startEdit(meaning)}>
                        <span className="order">{meaning.order}</span>
                        <span className="name">{meaning.name}</span>
                        <span className="keywords">
                          {meaning.upright.keywords.slice(0, 3).join(", ")}
                        </span>
                      </MeaningItem>
                    )
                  )}
                </MeaningList>
              </Section>
            );
          })}
        </div>
      </PageContent>
    </StyledPage>
  );
};

// Styled components
const StyledPage = styled("div", {
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "$wash",
  color: "#000",

  "& h1": {
    fontSize: "28px",
    fontFamily: "KeplerLightCondensed",
    marginBottom: "30px",
  },

  "& h2": {
    fontSize: "25px",
    fontFamily: "KeplerLightCondensed",
    fontWeight: "normal",
    marginBottom: "15px",
    opacity: 0.7,
    textTransform: "capitalize",
  },
});

const PageContent = styled("div", {
  flex: 1,
  overflow: "auto",
  paddingTop: "80px",
  paddingBottom: "40px",

  "& .container": {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "0 20px",
  },
});

const Toast = styled("div", {
  position: "fixed",
  top: "90px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "#e7e7e7",
  color: "#000000",
  padding: "10px 16px",
  borderRadius: "20px",
  fontSize: "12px",
  zIndex: 1000,
  animation: `${fadeIn} 0.3s ease forwards, ${fadeOut} 0.3s ease forwards 2s`,
});

const Section = styled("div", {
  marginBottom: "30px",
});

const MeaningList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const MeaningItem = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 15px",
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  borderRadius: "14px",
  fontSize: "14px",
  cursor: "pointer",
  transition: "border-color 0.2s",

  "&:hover": {
    borderColor: "#000",
  },

  "& .order": {
    width: "30px",
    opacity: 0.5,
    fontSize: "12px",
  },

  "& .name": {
    fontWeight: "500",
    width: "180px",
    flexShrink: 0,
  },

  "& .keywords": {
    flex: 1,
    fontSize: "13px",
    opacity: 0.6,
  },
});

const EditCard = styled("div", {
  position: "relative",
  padding: "20px",
  paddingTop: "45px",
  backgroundColor: "#fff",
  border: "1px solid #000",
  borderRadius: "14px",

  "& .meta-row": {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
  },

  "& .meaning-row": {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",

    "& .field": {
      flex: 1,
    },
  },

  "& .field": {
    marginBottom: "12px",

    "& label": {
      display: "block",
      fontSize: "12px",
      marginBottom: "5px",
      opacity: 0.6,
    },

    "& input, & textarea": {
      width: "100%",
      padding: "10px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      fontSize: "14px",
      fontFamily: "inherit",
    },

    "& textarea": {
      resize: "vertical",
      lineHeight: 1.5,
    },

    "&.small": {
      width: "100px",
      flexShrink: 0,
    },
  },

  "& .button-row": {
    display: "flex",
    gap: "10px",
  },
});

const SaveButton = styled("button", {
  padding: "10px 20px",
  backgroundColor: "#000",
  color: "#fff",
  border: "none",
  borderRadius: "11px",
  cursor: "pointer",
  "&:hover": { opacity: 0.8 },
});

const CancelButton = styled("button", {
  padding: "10px 20px",
  backgroundColor: "transparent",
  color: "#000",
  border: "1px solid #ddd",
  borderRadius: "11px",
  cursor: "pointer",
  "&:hover": { backgroundColor: "#f5f5f5" },
});

const AddButton = styled("button", {
  padding: "10px 20px",
  backgroundColor: "#fff",
  color: "#000",
  border: "1px solid #ddd",
  borderRadius: "11px",
  cursor: "pointer",
  marginBottom: "30px",
  fontSize: "14px",
  "&:hover": { borderColor: "#000" },
});

const DeleteButton = styled("button", {
  padding: "10px 20px",
  backgroundColor: "transparent",
  color: "#c00",
  border: "1px solid #fcc",
  borderRadius: "11px",
  cursor: "pointer",
  marginLeft: "auto",
  "&:hover": { backgroundColor: "#fff0f0", borderColor: "#c00" },
});

const MinimizeButton = styled("button", {
  position: "absolute",
  top: "12px",
  right: "12px",
  width: "24px",
  height: "24px",
  padding: 0,
  backgroundColor: "transparent",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "18px",
  fontWeight: "200",
  lineHeight: "20px",
  "&:hover": { backgroundColor: "#f5f5f5"},
});
